import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Grid,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Skeleton,
  Tooltip,
  LinearProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Refresh,
  ArrowBack,
  Business,
  Person,
  Bolt,
  Add,
  Phone,
  Email,
  Groups,
  Notes as NotesIcon,
  TaskAlt,
  PlayCircleOutline,
  CheckCircle,
  RadioButtonUnchecked,
  Description,
  ShoppingCart,
  LocalShipping,
  Receipt,
  Payments,
} from '@mui/icons-material';
import {
  PageHeader,
  StatusDot,
  TypePill,
  ErrorState,
  TableEmptyState,
  StatusTone,
} from '../../components/common/pageKit';
import { formatCurrency, formatIndianNumber, formatIndianDate, formatIndianDateTime } from '../../utils/currency';
import { useControlRoomColors } from '../../components/common/controlRoom';
import CrmAiAssist from '../../components/crm/CrmAiAssist';
import CrmAttachmentsPanel from '../../components/crm/CrmAttachmentsPanel';
import crmService, {
  OpportunityWorkspace,
  RelatedRecord,
  DealHealth,
  CrmActivity,
  Contact,
  FunnelStage,
  ActivityType,
  ACTIVITY_TYPES,
} from '../../services/crmService';

// ---------------------------------------------------------------------------
// The Opportunity Workspace — a 360-style page for a single deal. Replaces a
// plain edit form with the "connected ERP" story: deal economics + health,
// a next-best-action nudge, the Quotation→...→Payment traceability strip,
// the activity timeline, and the customer's buying committee.
// ---------------------------------------------------------------------------

/** Stage identity colour — mirrors PipelinePage's palette so a deal's stage
 * pill reads the same colour everywhere in the CRM module. */
const stageTints: Record<FunnelStage, string> = {
  PROSPECTING: '#64748b',
  QUALIFICATION: '#0284c7',
  PROPOSAL: '#7c3aed',
  NEGOTIATION: '#ea580c',
  WON: '#059669',
  LOST: '#dc2626',
};

const stageLabels: Record<FunnelStage, string> = {
  PROSPECTING: 'Prospecting',
  QUALIFICATION: 'Qualification',
  PROPOSAL: 'Proposal',
  NEGOTIATION: 'Negotiation',
  WON: 'Won',
  LOST: 'Lost',
};

const activityTints: Record<ActivityType, string> = {
  CALL: '#0284c7',
  EMAIL: '#7c3aed',
  MEETING: '#ea580c',
  NOTE: '#64748b',
  TASK: '#d97706',
  DEMO: '#059669',
};

const activityIcons: Record<ActivityType, React.ReactElement> = {
  CALL: <Phone sx={{ fontSize: 16 }} />,
  EMAIL: <Email sx={{ fontSize: 16 }} />,
  MEETING: <Groups sx={{ fontSize: 16 }} />,
  NOTE: <NotesIcon sx={{ fontSize: 16 }} />,
  TASK: <TaskAlt sx={{ fontSize: 16 }} />,
  DEMO: <PlayCircleOutline sx={{ fontSize: 16 }} />,
};

/** Icon per ERP traceability stage — falls back to a generic document glyph
 * for any stage name the backend adds later. */
const recordIcons: Record<string, React.ElementType> = {
  Quotation: Description,
  'Sales Order': ShoppingCart,
  Delivery: LocalShipping,
  Invoice: Receipt,
  Payment: Payments,
};

const healthTone = (band: DealHealth['band']): StatusTone => {
  if (band === 'On Track') return 'success';
  if (band === 'At Risk') return 'warning';
  if (band === 'Critical') return 'error';
  return 'neutral';
};

/** The header fields the API returns as a loose `Record<string, any>`,
 * narrowed to what this page actually reads. */
interface WorkspaceHeader {
  id: number;
  opportunityName: string;
  customerId: number | null;
  customerName: string | null;
  estimatedValue: number;
  probability: number;
  weighted: number;
  stage: FunnelStage;
  expectedClosureDate: string | null;
  assignedTo: number | null;
  ownerName: string | null;
  nextAction: string | null;
  nextActionAt: string | null;
  competitor: string | null;
  lossReason: string | null;
  leadSource: string | null;
}

const contactRoles = (c: Contact): Array<{ label: string; tint: string }> => {
  const roles: Array<{ label: string; tint: string }> = [];
  if (c.is_primary) roles.push({ label: 'Primary', tint: '#0284c7' });
  if (c.is_decision_maker) roles.push({ label: 'Decision Maker', tint: '#7c3aed' });
  if (c.is_influencer) roles.push({ label: 'Influencer', tint: '#ea580c' });
  if (c.is_technical) roles.push({ label: 'Technical', tint: '#059669' });
  if (c.is_finance) roles.push({ label: 'Finance', tint: '#d97706' });
  if (c.is_procurement) roles.push({ label: 'Procurement', tint: '#64748b' });
  return roles;
};

interface ActivityFormData {
  activityType: ActivityType;
  subject: string;
  notes: string;
  outcome: string;
  occurredAt: string;
  followUpAt: string;
}

const nowForInput = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

const emptyActivityForm = (): ActivityFormData => ({
  activityType: 'CALL',
  subject: '',
  notes: '',
  outcome: '',
  occurredAt: nowForInput(),
  followUpAt: '',
});

const OpportunityWorkspacePage: React.FC = () => {
  const { id: idParam } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const C = useControlRoomColors();

  const id = idParam ? parseInt(idParam, 10) : NaN;

  const [workspace, setWorkspace] = useState<OpportunityWorkspace | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<ActivityFormData>(emptyActivityForm());
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const fetchWorkspace = useCallback(async () => {
    if (!Number.isFinite(id)) {
      setError('Invalid opportunity — no id in the URL.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await crmService.getOpportunityWorkspace(id);
      setWorkspace(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load this opportunity workspace');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchWorkspace();
  }, [fetchWorkspace]);

  const openActivityDialog = (prefillSubject?: string) => {
    setForm({ ...emptyActivityForm(), subject: prefillSubject || '' });
    setDialogOpen(true);
  };

  const handleSaveActivity = async () => {
    if (!Number.isFinite(id)) return;
    setSaving(true);
    try {
      await crmService.createActivity({
        entityType: 'opportunity',
        entityId: id,
        activityType: form.activityType,
        subject: form.subject,
        notes: form.notes || undefined,
        outcome: form.outcome || undefined,
        occurredAt: form.occurredAt ? new Date(form.occurredAt).toISOString() : undefined,
        followUpAt: form.followUpAt ? new Date(form.followUpAt).toISOString() : undefined,
      });
      setToast({ message: 'Activity logged', severity: 'success' });
      setDialogOpen(false);
      await fetchWorkspace();
    } catch (err: any) {
      setToast({ message: err.message || 'Failed to log activity', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box>
        <Skeleton variant="text" width={280} height={44} />
        <Skeleton variant="text" width={180} height={24} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={180} sx={{ mb: 2.5, borderRadius: 3 }} />
        <Skeleton variant="rounded" height={64} sx={{ mb: 2.5, borderRadius: 3 }} />
        <Skeleton variant="rounded" height={140} sx={{ mb: 2.5, borderRadius: 3 }} />
        <Grid container spacing={2.5}>
          <Grid size={{ xs: 12, md: 7 }}>
            <Skeleton variant="rounded" height={320} sx={{ borderRadius: 3 }} />
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <Skeleton variant="rounded" height={320} sx={{ borderRadius: 3 }} />
          </Grid>
        </Grid>
      </Box>
    );
  }

  if (error || !workspace) {
    return (
      <Box>
        <PageHeader
          title="Opportunity Workspace"
          actions={
            <IconButton onClick={() => navigate('/crm/pipeline')} aria-label="Back to pipeline">
              <ArrowBack />
            </IconButton>
          }
        />
        <ErrorState message={error || undefined} onRetry={fetchWorkspace} />
      </Box>
    );
  }

  const header = workspace.header as WorkspaceHeader;
  const tint = stageTints[header.stage] || C.accent;

  return (
    <Box>
      <PageHeader
        title={header.opportunityName}
        subtitle={header.leadSource ? `Source: ${header.leadSource}` : undefined}
        actions={
          <>
            <Tooltip title="Back to Pipeline">
              <IconButton onClick={() => navigate('/crm/pipeline')} aria-label="Back to pipeline">
                <ArrowBack />
              </IconButton>
            </Tooltip>
            <Tooltip title="Refresh">
              <IconButton onClick={fetchWorkspace}>
                <Refresh />
              </IconButton>
            </Tooltip>
            <Button variant="contained" startIcon={<Add />} size="small" onClick={() => openActivityDialog()}>
              Log Activity
            </Button>
          </>
        }
      />

      {/* Hero band — deal economics + health */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 2.5,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          background: `linear-gradient(135deg, ${tint}14 0%, transparent 65%)`,
        }}
      >
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 8 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
              <TypePill label={stageLabels[header.stage] || header.stage} tint={tint} />
            </Stack>

            <Box
              onClick={() => header.customerId && navigate(`/crm/customer/${header.customerId}/360`)}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                cursor: header.customerId ? 'pointer' : 'default',
                color: header.customerId ? 'primary.main' : 'text.secondary',
                '&:hover': header.customerId ? { textDecoration: 'underline' } : undefined,
              }}
            >
              <Business sx={{ fontSize: 18 }} />
              <Typography sx={{ fontWeight: 700 }}>{header.customerName || 'No customer linked'}</Typography>
            </Box>

            <Grid container spacing={2.5} sx={{ mt: 0.5 }}>
              <Grid size={{ xs: 6, sm: 3 }} sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary">
                  Estimated Value
                </Typography>
                <Typography
                  title={formatCurrency(header.estimatedValue)}
                  sx={{ fontWeight: 800, fontSize: { xs: '1.15rem', sm: '1.3rem', md: '1.4rem' }, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                  {formatIndianNumber(header.estimatedValue)}
                </Typography>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }} sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary">
                  Weighted
                </Typography>
                <Typography
                  title={formatCurrency(header.weighted)}
                  sx={{ fontWeight: 800, fontSize: { xs: '1.15rem', sm: '1.3rem', md: '1.4rem' }, color: 'success.main', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                  {formatIndianNumber(header.weighted)}
                </Typography>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }} sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary">
                  Probability
                </Typography>
                <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.15rem', sm: '1.3rem', md: '1.4rem' }, fontVariantNumeric: 'tabular-nums' }}>
                  {header.probability}%
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, Math.max(0, header.probability))}
                  sx={{ height: 5, borderRadius: 3, mt: 0.5 }}
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  Expected Close
                </Typography>
                <Typography sx={{ fontWeight: 700 }}>
                  {header.expectedClosureDate ? formatIndianDate(new Date(header.expectedClosureDate)) : '—'}
                </Typography>
              </Grid>
            </Grid>

            <Stack direction="row" spacing={4} sx={{ mt: 2.5, flexWrap: 'wrap', rowGap: 1.5 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Owner
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Person sx={{ fontSize: 15, color: 'text.disabled' }} />
                  {header.ownerName || '—'}
                </Typography>
              </Box>
              {header.competitor && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Competitor
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {header.competitor}
                  </Typography>
                </Box>
              )}
              {header.lossReason && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Loss Reason
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'error.main' }}>
                    {header.lossReason}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Paper
              elevation={0}
              sx={{ p: 2, borderRadius: 2.5, border: '1px solid', borderColor: 'divider', height: '100%', bgcolor: 'background.paper' }}
            >
              <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: '0.06em' }}>
                Deal Health
              </Typography>
              <Stack direction="row" alignItems="baseline" spacing={1.25} sx={{ mt: 0.5, mb: 1.25 }}>
                <Typography sx={{ fontWeight: 800, fontSize: '2rem', fontVariantNumeric: 'tabular-nums' }}>
                  {workspace.health.score}
                </Typography>
                <StatusDot label={workspace.health.band} tone={healthTone(workspace.health.band)} />
              </Stack>
              {workspace.health.reasons.length > 0 ? (
                <Stack spacing={0.5}>
                  {workspace.health.reasons.map((r, i) => (
                    <Typography key={i} variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      • {r}
                    </Typography>
                  ))}
                </Stack>
              ) : (
                <Typography variant="caption" color="text.disabled">
                  No health signals recorded.
                </Typography>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      {/* Next best action */}
      {workspace.nextBestAction && (
        <Paper
          elevation={0}
          sx={{
            p: 2.25,
            mb: 2.5,
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
            bgcolor: `${C.accent}0d`,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <Bolt sx={{ color: 'primary.main', mt: 0.25, flexShrink: 0 }} />
            <Box>
              <Typography sx={{ fontWeight: 700 }}>{workspace.nextBestAction.action}</Typography>
              <Typography variant="body2" color="text.secondary">
                {workspace.nextBestAction.reason}
              </Typography>
            </Box>
          </Stack>
          <Button
            variant="contained"
            size="small"
            onClick={() => openActivityDialog(workspace.nextBestAction ? workspace.nextBestAction.action : undefined)}
          >
            Log Activity
          </Button>
        </Paper>
      )}

      {/* ERP traceability strip — Quotation -> Sales Order -> Delivery -> Invoice -> Payment */}
      <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
          ERP Traceability
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'stretch', overflowX: 'auto', pb: 0.5 }}>
          {workspace.relatedRecords.map((rec: RelatedRecord, i: number) => {
            const RecIcon = recordIcons[rec.stage] || Description;
            return (
              <React.Fragment key={rec.stage}>
                <Box
                  role={rec.present && rec.link ? 'button' : undefined}
                  tabIndex={rec.present && rec.link ? 0 : undefined}
                  onClick={() => rec.present && rec.link && navigate(rec.link)}
                  onKeyDown={(e) => {
                    if (rec.present && rec.link && (e.key === 'Enter' || e.key === ' ')) navigate(rec.link);
                  }}
                  sx={{
                    minWidth: 172,
                    flexShrink: 0,
                    p: 1.75,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: rec.present ? 'success.main' : 'divider',
                    bgcolor: rec.present ? 'rgba(5, 150, 105, 0.06)' : 'action.hover',
                    opacity: rec.present ? 1 : 0.6,
                    cursor: rec.present && rec.link ? 'pointer' : 'default',
                    transition: 'box-shadow .15s ease',
                    '&:hover': rec.present && rec.link ? { boxShadow: 2 } : undefined,
                    '&:focus-visible': rec.present && rec.link ? { outline: `2px solid ${tint}`, outlineOffset: 1 } : undefined,
                  }}
                >
                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.75 }}>
                    {rec.present ? (
                      <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                    ) : (
                      <RadioButtonUnchecked sx={{ fontSize: 16, color: 'text.disabled' }} />
                    )}
                    <RecIcon sx={{ fontSize: 15, color: rec.present ? 'text.secondary' : 'text.disabled' }} />
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        color: rec.present ? 'text.primary' : 'text.disabled',
                      }}
                    >
                      {rec.stage}
                    </Typography>
                  </Stack>
                  {rec.present ? (
                    <>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.8125rem' }} noWrap>
                        {rec.docNumber || '—'}
                      </Typography>
                      <Typography
                        title={rec.amount !== null ? formatCurrency(rec.amount) : undefined}
                        sx={{ fontWeight: 700, fontSize: '0.875rem', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                      >
                        {rec.amount !== null ? formatIndianNumber(rec.amount) : '—'}
                      </Typography>
                      {rec.status && (
                        <Box sx={{ mt: 0.5 }}>
                          <TypePill label={rec.status} tint="#0284c7" />
                        </Box>
                      )}
                    </>
                  ) : (
                    <Typography variant="caption" color="text.disabled">
                      Not yet
                    </Typography>
                  )}
                </Box>
                {i < workspace.relatedRecords.length - 1 && (
                  <Box sx={{ width: 28, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Box sx={{ width: '100%', height: 2, bgcolor: rec.present ? 'success.main' : 'divider' }} />
                  </Box>
                )}
              </React.Fragment>
            );
          })}
        </Box>
      </Paper>

      {/* Timeline + buying committee */}
      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, md: 7 }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Timeline
              </Typography>
              <Button size="small" startIcon={<Add />} onClick={() => openActivityDialog()}>
                Add Activity
              </Button>
            </Stack>
            {workspace.timeline.length === 0 ? (
              <TableEmptyState title="No activity logged yet" hint="Log a call, email, meeting or note to start the timeline." />
            ) : (
              <Stack spacing={0}>
                {workspace.timeline.map((a: CrmActivity, i: number) => (
                  <Box key={a.id} sx={{ display: 'flex', gap: 1.5, pb: i < workspace.timeline.length - 1 ? 2 : 0 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <Box
                        sx={{
                          width: 30,
                          height: 30,
                          borderRadius: '50%',
                          display: 'grid',
                          placeItems: 'center',
                          backgroundColor: `${activityTints[a.activity_type]}1a`,
                          color: activityTints[a.activity_type],
                          flexShrink: 0,
                        }}
                      >
                        {activityIcons[a.activity_type]}
                      </Box>
                      {i < workspace.timeline.length - 1 && (
                        <Box sx={{ width: 2, flexGrow: 1, bgcolor: 'divider', mt: 0.5, minHeight: 12 }} />
                      )}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0, pb: 0.5 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                        <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>{a.subject}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                          {formatIndianDateTime(new Date(a.occurred_at))}
                        </Typography>
                      </Stack>
                      <Box sx={{ mt: 0.5 }}>
                        <TypePill label={a.activity_type} tint={activityTints[a.activity_type]} />
                      </Box>
                      {a.notes && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {a.notes}
                        </Typography>
                      )}
                      {a.outcome && (
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontWeight: 600 }}>
                          Outcome: {a.outcome}
                        </Typography>
                      )}
                      {a.owner_name && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}
                        >
                          <Person sx={{ fontSize: 12 }} /> {a.owner_name}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ))}
              </Stack>
            )}
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 5 }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
              Buying Committee
            </Typography>
            {workspace.contacts.length === 0 ? (
              <TableEmptyState title="No contacts on file" hint="Add contacts on the customer's 360 to build the buying committee." />
            ) : (
              <Stack spacing={1.5}>
                {workspace.contacts.map((c: Contact) => (
                  <Box key={c.id} sx={{ p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.875rem' }}>{c.name}</Typography>
                    {(c.designation || c.department) && (
                      <Typography variant="caption" color="text.secondary">
                        {[c.designation, c.department].filter(Boolean).join(' · ')}
                      </Typography>
                    )}
                    <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
                      {contactRoles(c).map((r) => (
                        <TypePill key={r.label} label={r.label} tint={r.tint} />
                      ))}
                    </Stack>
                    {(c.email || c.mobile || c.phone) && (
                      <Stack direction="row" spacing={2} sx={{ mt: 1, flexWrap: 'wrap' }}>
                        {c.email && (
                          <Typography variant="caption" color="text.secondary">
                            {c.email}
                          </Typography>
                        )}
                        {(c.mobile || c.phone) && (
                          <Typography variant="caption" color="text.secondary">
                            {c.mobile || c.phone}
                          </Typography>
                        )}
                      </Stack>
                    )}
                  </Box>
                ))}
              </Stack>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* AI assist + document links */}
      {Number.isFinite(id) && (
        <Box sx={{ mt: 3, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          <CrmAiAssist mode="opportunity" id={id} />
          <CrmAttachmentsPanel entityType="opportunity" entityId={id} />
        </Box>
      )}

      {/* Add activity dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Log Activity</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              select
              fullWidth
              label="Activity Type"
              value={form.activityType}
              onChange={(e) => setForm((f) => ({ ...f, activityType: e.target.value as ActivityType }))}
            >
              {ACTIVITY_TYPES.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              label="Subject"
              value={form.subject}
              onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
              required
            />
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Notes"
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            />
            <TextField
              fullWidth
              label="Outcome"
              value={form.outcome}
              onChange={(e) => setForm((f) => ({ ...f, outcome: e.target.value }))}
            />
            <TextField
              fullWidth
              label="Occurred At"
              type="datetime-local"
              value={form.occurredAt}
              onChange={(e) => setForm((f) => ({ ...f, occurredAt: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              label="Follow-up At"
              type="datetime-local"
              value={form.followUpAt}
              onChange={(e) => setForm((f) => ({ ...f, followUpAt: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              helperText="Optional — leave blank if no follow-up is needed"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveActivity} disabled={saving || !form.subject.trim()}>
            {saving ? 'Saving…' : 'Log Activity'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default OpportunityWorkspacePage;
